import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactFlow, {
  Node,
  Edge,
  useNodesState,
  useEdgesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Users, Shield, Cloud, Briefcase, Cog, TrendingUp, CheckCircle } from 'lucide-react';

// Componente para nodos principales (áreas) - MINIMALISTA
const AreaNode = ({ data }: { data: any }) => {
  return (
    <div
      className="px-5 py-3 bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 rounded-lg shadow-sm hover:shadow-md hover:border-neutral-400 dark:hover:border-neutral-600 transition-all cursor-pointer"
      style={{ width: '180px' }}
      onClick={data.onClick}
      title={data.description}
    >
      <div className="flex items-center gap-2.5">
        <div
          className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
          style={{ color: data.color }}
        >
          {React.cloneElement(data.icon, { size: 16 })}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-50 truncate">
            {data.label}
          </div>
          {data.subCount && (
            <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
              {data.subCount} servicios {data.hasSubServices && (data.isExpanded ? '▼' : '▶')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Componente para sub-servicios - MINIMALISTA
const SubServiceNode = ({ data }: { data: any }) => {
  return (
    <div
      className="px-4 py-2 bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-md hover:bg-white dark:hover:bg-neutral-900 hover:border-neutral-300 dark:hover:border-neutral-600 transition-all cursor-pointer"
      style={{ width: '140px' }}
      onClick={data.onClick}
      title={data.description}
    >
      <div className="flex items-center gap-2">
        <div
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: data.color }}
        ></div>
        <div className="text-xs font-medium text-neutral-700 dark:text-neutral-300 truncate">
          {data.label}
        </div>
      </div>
    </div>
  );
};

// Componente personalizado para el nodo central - MINIMALISTA
const CentralNode = ({ data }: { data: any }) => {
  return (
    <div className="px-6 py-4 bg-primary dark:bg-primary-dark rounded-lg shadow-md" style={{ width: '140px' }}>
      <div className="text-center">
        <div className="text-white font-bold text-lg">{data.label}</div>
        <div className="text-white/80 text-xs font-medium mt-1">Ecosystem</div>
      </div>
    </div>
  );
};

const nodeTypes = {
  central: CentralNode,
  area: AreaNode,
  subservice: SubServiceNode,
};

interface HomeProps {
  onNavigateToArea?: (areaName: string) => void;
}

const Home: React.FC<HomeProps> = ({ onNavigateToArea }) => {
  const navigate = useNavigate();
  const [expandedAreas, setExpandedAreas] = useState<Set<string>>(new Set());

  const areaNameToId = useMemo((): { [key: string]: number } => ({
    'azure-platform': 1,
    'data': 2,
    'secure': 3,
    'low-code': 4,
    'pro-code': 5,
    'modern-work': 6,
    'consulting': 7,
    'outsourcing': 8,
    'demand-hub': 9,
    'empowering-humans': 2,
    'cloud': 1,
    'service-delivery': 7,
    'change-management': 7,
    'quality-compliance': 7,
    'demand-hub-eh': 9,
    'demand-hub-secure': 9,
    'demand-hub-cloud': 9,
    'secure-service': 3
  }), []);

  const areaIdToName = useMemo((): { [key: number]: string } => ({
    1: "Azure Platform",
    2: "Data",
    3: "Secure",
    4: "Low Code",
    5: "Pro Code",
    6: "Modern Work",
    7: "Consulting",
    8: "OutSourcing",
    9: "Demand Hub"
  }), []);

  const navigateToArea = useCallback((areaName: string) => {
    const areaId = areaNameToId[areaName];
    if (areaId) {
      const realName = areaIdToName[areaId] || areaName.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      navigate(`/area/${areaId}?name=${encodeURIComponent(realName)}`);
    }
  }, [navigate, areaNameToId, areaIdToName]);

  const toggleArea = useCallback((areaId: string) => {
    const newExpanded = new Set(expandedAreas);
    if (newExpanded.has(areaId)) {
      newExpanded.delete(areaId);
    } else {
      newExpanded.add(areaId);
    }
    setExpandedAreas(newExpanded);
  }, [expandedAreas]);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    const visibleNodes: Node[] = [
    {
      id: 'servicios',
      type: 'central',
      position: { x: 600, y: 50 },
      data: { label: 'SERVICIOS' },
      draggable: false,
    },
    {
      id: 'empowering-humans',
      type: 'area',
      position: { x: 50, y: 180 },
      data: {
        label: 'Empowering Humans',
        color: '#10B981',
        icon: <Users size={18} />,
        subCount: 4,
        hasSubServices: true,
        isExpanded: expandedAreas.has('empowering-humans'),
        description: 'Potenciación de capacidades humanas a través de datos y tecnología',
        onClick: (e: any) => {
          e.stopPropagation();
          toggleArea('empowering-humans');
        },
      },
      draggable: false,
    },
    {
      id: 'secure',
      type: 'area',
      position: { x: 280, y: 180 },
      data: {
        label: 'Secure',
        color: '#EF4444',
        icon: <Shield size={18} />,
        subCount: 2,
        hasSubServices: true,
        isExpanded: expandedAreas.has('secure'),
        description: 'Ciberseguridad integral y protección de activos digitales',
        onClick: (e: any) => {
          e.stopPropagation();
          toggleArea('secure');
        },
      },
      draggable: false,
    },
    {
      id: 'cloud',
      type: 'area',
      position: { x: 510, y: 180 },
      data: {
        label: 'Cloud',
        color: '#3B82F6',
        icon: <Cloud size={18} />,
        subCount: 3,
        hasSubServices: true,
        isExpanded: expandedAreas.has('cloud'),
        description: 'Infraestructura y servicios en la nube (Azure, Modern Work)',
        onClick: (e: any) => {
          e.stopPropagation();
          toggleArea('cloud');
        },
      },
      draggable: false,
    },
    {
      id: 'service-delivery',
      type: 'area',
      position: { x: 740, y: 180 },
      data: {
        label: 'Service Delivery',
        color: '#F59E0B',
        icon: <Cog size={18} />,
        description: 'Entrega y gestión de servicios de TI',
        onClick: (e: any) => {
          e.stopPropagation();
          navigateToArea('service-delivery');
        },
      },
      draggable: false,
    },
    {
      id: 'change-management',
      type: 'area',
      position: { x: 970, y: 180 },
      data: {
        label: 'Change Management',
        color: '#06B6D4',
        icon: <TrendingUp size={18} />,
        description: 'Gestión del cambio organizacional y tecnológico',
        onClick: (e: any) => {
          e.stopPropagation();
          navigateToArea('change-management');
        },
      },
      draggable: false,
    },

    ...(expandedAreas.has('empowering-humans') ? [
      {
        id: 'data',
        type: 'subservice' as const,
        position: { x: 50, y: 280 },
        data: {
          label: 'Data',
          color: '#10B981',
          description: 'Analytics, BI, Data Engineering y Machine Learning',
          onClick: () => navigateToArea('data')
        },
        draggable: false,
      },
      {
        id: 'low-code',
        type: 'subservice' as const,
        position: { x: 50, y: 340 },
        data: {
          label: 'Low Code',
          color: '#10B981',
          description: 'Plataformas de desarrollo con poco código (Power Platform)',
          onClick: () => navigateToArea('low-code')
        },
        draggable: false,
      },
      {
        id: 'pro-code',
        type: 'subservice' as const,
        position: { x: 50, y: 400 },
        data: {
          label: 'Pro Code',
          color: '#10B981',
          description: 'Desarrollo de software tradicional y aplicaciones complejas',
          onClick: () => navigateToArea('pro-code')
        },
        draggable: false,
      },
      {
        id: 'demand-hub-eh',
        type: 'subservice' as const,
        position: { x: 50, y: 460 },
        data: {
          label: 'Demand Hub',
          color: '#10B981',
          description: 'Gestión de demanda y solicitudes para Empowering Humans',
          onClick: () => navigateToArea('demand-hub-eh')
        },
        draggable: false,
      },
    ] : []),

    ...(expandedAreas.has('secure') ? [
      {
        id: 'secure-service',
        type: 'subservice' as const,
        position: { x: 280, y: 280 },
        data: {
          label: 'Secure Service',
          color: '#EF4444',
          description: 'Servicios de ciberseguridad, auditorías y protección',
          onClick: () => navigateToArea('secure-service')
        },
        draggable: false,
      },
      {
        id: 'demand-hub-secure',
        type: 'subservice' as const,
        position: { x: 280, y: 340 },
        data: {
          label: 'Demand Hub',
          color: '#EF4444',
          description: 'Gestión de solicitudes de seguridad y compliance',
          onClick: () => navigateToArea('demand-hub-secure')
        },
        draggable: false,
      },
    ] : []),

    ...(expandedAreas.has('cloud') ? [
      {
        id: 'azure-platform',
        type: 'subservice' as const,
        position: { x: 510, y: 280 },
        data: {
          label: 'Azure Platform',
          color: '#3B82F6',
          description: 'Infraestructura Azure, DevOps y servicios en la nube',
          onClick: () => navigateToArea('azure-platform')
        },
        draggable: false,
      },
      {
        id: 'modern-work',
        type: 'subservice' as const,
        position: { x: 510, y: 340 },
        data: {
          label: 'Modern Work',
          color: '#3B82F6',
          description: 'Microsoft 365, Teams, colaboración y productividad',
          onClick: () => navigateToArea('modern-work')
        },
        draggable: false,
      },
      {
        id: 'demand-hub-cloud',
        type: 'subservice' as const,
        position: { x: 510, y: 400 },
        data: {
          label: 'Demand Hub',
          color: '#3B82F6',
          description: 'Gestión de solicitudes de infraestructura cloud',
          onClick: () => navigateToArea('demand-hub-cloud')
        },
        draggable: false,
      },
    ] : []),

    {
      id: 'quality-compliance',
      type: 'area',
      position: { x: 740, y: 270 },
      data: {
        label: 'Product Quality & Compliance',
        color: '#EC4899',
        icon: <CheckCircle size={18} />,
        description: 'Calidad de productos, cumplimiento normativo y auditorías',
        onClick: (e: any) => {
          e.stopPropagation();
          navigateToArea('quality-compliance');
        },
      },
      draggable: false,
    },
    {
      id: 'outsourcing',
      type: 'area',
      position: { x: 970, y: 270 },
      data: {
        label: 'Outsourcing',
        color: '#8B5CF6',
        icon: <Briefcase size={18} />,
        description: 'Externalización de procesos y gestión de proveedores',
        onClick: (e: any) => {
          e.stopPropagation();
          navigateToArea('outsourcing');
        },
      },
      draggable: false,
    },
  ];

    const visibleEdges: Edge[] = [
    { id: 'servicios-eh', source: 'servicios', target: 'empowering-humans', animated: false, style: { stroke: '#D4D4D4', strokeWidth: 1.5 }, type: 'smoothstep' },
    { id: 'servicios-secure', source: 'servicios', target: 'secure', animated: false, style: { stroke: '#D4D4D4', strokeWidth: 1.5 }, type: 'smoothstep' },
    { id: 'servicios-cloud', source: 'servicios', target: 'cloud', animated: false, style: { stroke: '#D4D4D4', strokeWidth: 1.5 }, type: 'smoothstep' },
    { id: 'servicios-sd', source: 'servicios', target: 'service-delivery', animated: false, style: { stroke: '#D4D4D4', strokeWidth: 1.5 }, type: 'smoothstep' },
    { id: 'servicios-cm', source: 'servicios', target: 'change-management', animated: false, style: { stroke: '#D4D4D4', strokeWidth: 1.5 }, type: 'smoothstep' },
    { id: 'servicios-qc', source: 'servicios', target: 'quality-compliance', animated: false, style: { stroke: '#D4D4D4', strokeWidth: 1.5 }, type: 'smoothstep' },
    { id: 'servicios-out', source: 'servicios', target: 'outsourcing', animated: false, style: { stroke: '#D4D4D4', strokeWidth: 1.5 }, type: 'smoothstep' },

    ...(expandedAreas.has('empowering-humans') ? [
      { id: 'eh-data', source: 'empowering-humans', target: 'data', animated: false, style: { stroke: '#D4D4D4', strokeWidth: 1 }, type: 'smoothstep' },
      { id: 'eh-lowcode', source: 'empowering-humans', target: 'low-code', animated: false, style: { stroke: '#D4D4D4', strokeWidth: 1 }, type: 'smoothstep' },
      { id: 'eh-procode', source: 'empowering-humans', target: 'pro-code', animated: false, style: { stroke: '#D4D4D4', strokeWidth: 1 }, type: 'smoothstep' },
      { id: 'eh-demand', source: 'empowering-humans', target: 'demand-hub-eh', animated: false, style: { stroke: '#D4D4D4', strokeWidth: 1 }, type: 'smoothstep' },
    ] : []),

    ...(expandedAreas.has('secure') ? [
      { id: 'sec-service', source: 'secure', target: 'secure-service', animated: false, style: { stroke: '#D4D4D4', strokeWidth: 1 }, type: 'smoothstep' },
      { id: 'sec-demand', source: 'secure', target: 'demand-hub-secure', animated: false, style: { stroke: '#D4D4D4', strokeWidth: 1 }, type: 'smoothstep' },
    ] : []),

    ...(expandedAreas.has('cloud') ? [
      { id: 'cloud-azure', source: 'cloud', target: 'azure-platform', animated: false, style: { stroke: '#D4D4D4', strokeWidth: 1 }, type: 'smoothstep' },
      { id: 'cloud-mw', source: 'cloud', target: 'modern-work', animated: false, style: { stroke: '#D4D4D4', strokeWidth: 1 }, type: 'smoothstep' },
      { id: 'cloud-demand', source: 'cloud', target: 'demand-hub-cloud', animated: false, style: { stroke: '#D4D4D4', strokeWidth: 1 }, type: 'smoothstep' },
    ] : []),
  ];

    setNodes(visibleNodes);
    setEdges(visibleEdges);
  }, [expandedAreas, toggleArea, navigateToArea, setNodes, setEdges]);

  return (
    <div className="w-full h-screen bg-neutral-50 dark:bg-neutral-900">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.5}
        maxZoom={1.5}
        defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
        proOptions={{ hideAttribution: true }}
      />
    </div>
  );
};

export default Home;
